/**
 * Tech Support (1.5.0): the FORA technology support associate. One day a week to start (Admin → Settings → Tech
 * Support: the day(s), the hours, where appointments are held, their length, who gets the ticket).
 *
 * Book → Tech Support: tenant → a time on a tech day → Where → What they need help with → Device → confirm. The
 * tenant gets "Tech booked" (E2) and the morning-of reminder (E3) with their own link; the associate gets the
 * ticket (E1) with Open ticket and Call the tenant. On the day: Arrived → Close out (what was worked on, found,
 * done, recommended; the outcome, and why when it is not fully resolved; follow-up; the boundary checklist — all
 * three ticked to save). Counts for ABF only, never VISIONS. The Queue 1 processor books housekeeping only, so
 * these rows are written by the app's own identity, like the Companion visits (docs/DECISIONS.md D-1.5.0).
 */
import { z } from 'zod';
import { safeTechText } from './tech-kit';
import { isDateKey } from './dates';
import { TECH_PLACES, type TechPlace } from './settings-catalog';

export const TECH_PROGRAM = { ProgramCode: 'TECH', ProgramName: 'Tech Support', SM_Programs: 'Tech Support', RequiresVolunteer: false, RequiresHousekeepingSlot: false, Active: true } as const;
export const TECH_CODE = 'TECH';

/** What they need help with (the pick-list) — also the devices a close-out says were worked on. */
export const HELP_KEYS = ['phone', 'computer', 'tablet', 'tv', 'internet', 'printer', 'speaker', 'screenReader', 'smartHome', 'other'] as const;
export type HelpKey = (typeof HELP_KEYS)[number];

export const HELP_WORDS: Record<HelpKey, { en: string; es: string }> = {
  phone: { en: 'Phone', es: 'Teléfono' },
  computer: { en: 'Computer', es: 'Computadora' },
  tablet: { en: 'Tablet', es: 'Tableta' },
  tv: { en: 'TV / cable box', es: 'Televisor / caja de cable' },
  internet: { en: 'Internet / Wi-Fi', es: 'Internet / Wi-Fi' },
  printer: { en: 'Printer', es: 'Impresora' },
  speaker: { en: 'Smart speaker / voice assistant', es: 'Altavoz inteligente / asistente de voz' },
  screenReader: { en: 'Screen reader / magnifier software', es: 'Lector de pantalla / programa de ampliación' },
  smartHome: { en: 'Smart home (thermostat, plugs or lights, doorbell or camera, lock)', es: 'Casa inteligente (termostato, enchufes o luces, timbre o cámara, cerradura)' },
  other: { en: 'Other', es: 'Otro' },
};

export const OUTCOMES = ['resolved', 'partly', 'notResolved'] as const;
export type TechOutcome = (typeof OUTCOMES)[number];

/** Why it is not fully resolved. */
export const WHY_KEYS = ['part', 'vendor', 'access', 'time', 'stopped', 'scope', 'other'] as const;
export type WhyKey = (typeof WHY_KEYS)[number];
export const WHY_WORDS: Record<WhyKey, string> = {
  part: 'Needs a part or a purchase',
  vendor: 'Needs the vendor or the carrier',
  access: 'A password or account access is missing',
  time: 'Ran out of time',
  stopped: 'The tenant chose to stop',
  scope: 'Beyond what Tech Support does',
  other: 'Other',
};

/** The boundary checklist: all three ticked to save a close-out. */
export const BOUNDARY_KEYS = ['noPasswordsKept', 'nothingInstalledWithoutConsent', 'settingsOnlyWithOk'] as const;
export type BoundaryKey = (typeof BOUNDARY_KEYS)[number];

export const PLACE_WORDS: Record<TechPlace, { en: string; es: string }> = {
  Boardroom: { en: 'the Boardroom', es: 'la sala de juntas' },
  Office: { en: 'the Office', es: 'la oficina' },
  Apartment: { en: 'your apartment', es: 'su apartamento' },
};

const text = (max: number) => z.string().trim().max(max);

export const TechBookSchema = z
  .object({
    tenantId: z.string().trim().min(1).max(60),
    dayKey: z.string().refine(isDateKey, 'yyyyMMdd'),
    minutes: z.number().int().min(0).max(24 * 60),
    place: z.enum(TECH_PLACES),
    help: z.array(z.enum(HELP_KEYS)).min(1, 'Choose what they need help with.').max(HELP_KEYS.length),
    other: text(200).default(''),
    device: text(200).min(1, 'Name the device (for example “iPhone 12”).'),
    description: text(2000).default(''),
    emailTenant: z.boolean().default(true),
  })
  .superRefine((b, ctx) => {
    if (b.help.includes('other') && !b.other) ctx.addIssue({ code: 'custom', path: ['other'], message: 'Say what “other” is.' });
  });
export type TechBookInput = z.input<typeof TechBookSchema>;

export const TechCloseoutSchema = z
  .object({
    devices: z.array(z.enum(HELP_KEYS)).min(1, 'Choose the device(s) worked on.'),
    devicesOther: text(200).default(''),
    found: text(2000).min(1, 'Say what you found.'),
    done: text(2000).min(1, 'Say what was done.'),
    next: text(2000).default(''),
    outcome: z.enum(OUTCOMES),
    why: z.enum(WHY_KEYS).nullable().default(null),
    whyOther: text(500).default(''),
    wouldResolve: text(1000).default(''),
    followUp: z.object({ needed: z.boolean(), what: text(1000).default(''), who: text(200).default('') }),
    finishedAt: z.string().regex(/^\d{2}:\d{2}$/, 'hh:mm'),
    tenantPresent: z.boolean(),
    tenantCanRepeat: z.boolean(),
    boundary: z.object({ noPasswordsKept: z.boolean(), nothingInstalledWithoutConsent: z.boolean(), settingsOnlyWithOk: z.boolean() }),
    /**
     * 1.8.5 (D-1.8.5-5): the after-visit summary — exactly what the tech approved in the box (a draft from "Help me
     * write it" is only ever a suggestion in the sheet; nothing is saved until Save). Kept as written. Optional: a
     * close-out from before 1.8.5 has none, and the found / done boxes above already carry the facts.
     */
    summary: text(4000).default(''),
    /** 1.8.5: the After photos sent with this close-out (paths in the ticket folder's After/) — optional. */
    afterPhotos: z.array(z.string().trim().min(1).max(400)).max(24).default([]),
  })
  .superRefine((c, ctx) => {
    for (const key of ['devicesOther', 'found', 'done', 'next', 'whyOther', 'wouldResolve', 'summary'] as const) if (!safeTechText(4000).safeParse(c[key]).success) ctx.addIssue({ code: 'custom', path: [key], message: 'Remove credentials or financial information.' });
    for (const key of ['what', 'who'] as const) if (!safeTechText(1000).safeParse(c.followUp[key]).success) ctx.addIssue({ code: 'custom', path: ['followUp', key], message: 'Remove credentials or financial information.' });
    if (c.devices.includes('other') && !c.devicesOther) ctx.addIssue({ code: 'custom', path: ['devicesOther'], message: 'Say what the other device was (for example “talking microwave”).' });
    if (c.outcome !== 'resolved') {
      if (!c.why) ctx.addIssue({ code: 'custom', path: ['why'], message: 'Say why it is not fully resolved.' });
      if (c.why === 'other' && !c.whyOther) ctx.addIssue({ code: 'custom', path: ['whyOther'], message: 'Say why.' });
      if (!c.wouldResolve) ctx.addIssue({ code: 'custom', path: ['wouldResolve'], message: 'Say what would resolve it.' });
    }
    if (c.followUp.needed && !c.followUp.what) ctx.addIssue({ code: 'custom', path: ['followUp', 'what'], message: 'Say what the follow-up is.' });
    for (const k of BOUNDARY_KEYS) if (!c.boundary[k]) ctx.addIssue({ code: 'custom', path: ['boundary', k], message: 'All three boundary checks must be ticked to save.' });
  });
export type TechCloseoutInput = z.input<typeof TechCloseoutSchema>;
export type TechCloseout = z.output<typeof TechCloseoutSchema>;

export interface TechSlot {
  dayKey: string;
  minutes: number;
  endMinutes: number;
  state: 'free' | 'taken' | 'passed';
}

export interface TechDay {
  dayKey: string;
  slots: TechSlot[];
  closed: string | null;
}

export interface TechSlotsResponse {
  ready: boolean;
  /** Why Tech Support cannot be booked yet (the program row, the ticket recipient…). */
  notReady: string[];
  days: TechDay[];
  places: TechPlace[];
  lengthMinutes: number;
}

export interface TechTicketView {
  appointmentId: string;
  dayKey: string;
  minutes: number;
  endMinutes: number;
  place: TechPlace;
  /** First name only on the associate's screens; the office sees the full name. */
  tenantFirst: string;
  tenantName: string | null;
  unit: string;
  /** The ticket's Call button only (never shown as text to the associate). */
  phone: string | null;
  help: HelpKey[];
  other: string;
  device: string;
  description: string;
  status: string;
  confirmation: string;
  techEmail: string | null;
  arrivedAt: string | null;
  closeout: (TechCloseout & { by: string; at: string }) | null;
  folder: string;
  /** 1.5.3: not confirmed until the tenant accepts the Tech Support agreement (none on file yet). */
  awaitingAgreement?: boolean;
  /** 1.6.8: booked, not started, and the tenant has no completed agreement — "Agreement not signed — the visit cannot start"; Arrived and Close out are off (the server refuses them too). */
  agreementMissing?: boolean;
  /** 1.8.5 (D-1.8.5-3): booked and not opened yet by the ticket's tech — "New". */
  isNew?: boolean;
  /** 1.8.5: when the ticket's tech first opened it (null: not yet; absent on a ticket read before 1.8.5). */
  openedAt?: string | null;
  /** 1.8.5 (D-1.8.5-4): "Not home" at the door — no entry; the ticket went back to the office as Needs a new day. */
  notHome?: { at: string; by: string } | null;
  /** 1.8.5: the ticket's photos (Open ticket only), Before (at the start) and After (with the close-out). */
  photos?: { before: TechPhoto[]; after: TechPhoto[] };
}

/** A photo in the ticket's folder (Before/ or After/). */
export interface TechPhoto {
  name: string;
  path: string;
}

export interface TechTodayResponse {
  day: string;
  tickets: TechTicketView[];
}

/** "Tech Support/2026-10-14 Maria 6C" — the ticket's folder (date first, REQUIREMENTS §9). */
export function techFolder(dayKey: string, first: string, unit: string): string {
  const clean = (s: string) => s.replace(/[\\/:*?"<>|#%]/g, ' ').replace(/\s+/g, ' ').trim();
  return `Tech Support/${dayKey.slice(0, 4)}-${dayKey.slice(4, 6)}-${dayKey.slice(6)} ${clean(first) || 'Tenant'} ${clean(unit)}`.trim();
}

export function helpList(keys: readonly HelpKey[], other: string, lang: 'en' | 'es'): string {
  return keys.map((k) => (k === 'other' ? other || HELP_WORDS.other[lang] : HELP_WORDS[k][lang])).join(', ');
}
